import { Copy, FileText, Info, Pencil, PowerOff } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { JsonTable } from "~/components/json-table";
import { SectionCard } from "~/components/section-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
} from "~/components/ui";
import { toAreaGeoItemEntryRows, useAreaGeoItemDetail, useGeoItemActivation } from "~/features/recordGeoItems/hooks";
import { parseGeometry } from "~/features/recordGeoItems/utils/parseGeometry";
import { parseH3IndexesStr } from "~/features/recordGeoItems/utils/parseH3IndexStr";
import { v2Route } from "~/v2/routes";
import { AreaDetailField, AreaGeoItemEntryList } from "./AreaGeoItemEntryList";
import { AreaViewMap } from "./AreaViewMap";

interface AreaGeoItemDetailProps {
  recordId: string;
  geoItemId: string;
}

export function AreaGeoItemDetail({ recordId, geoItemId }: AreaGeoItemDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const navigate = useNavigate();
  const rgi = useAreaGeoItemDetail(recordId, geoItemId);
  const { onDeactivate, processing } = useGeoItemActivation({ recordId, geoItemId });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const areaH3Indexes = useMemo(() => parseH3IndexesStr(rgi?.geoItem.h3Indexes ?? null), [rgi]);
  const geometry = useMemo(() => parseGeometry(rgi?.geoItem.geometry ?? null), [rgi]);

  if (!rgi) {
    return <p className="text-[13px] text-fg-3">{t("ui.notFound")}</p>;
  }

  const entries = toAreaGeoItemEntryRows(rgi.entries, locale);
  const evidences = rgi.evidences;

  const handleConfirmDeactivate = async () => {
    const ok = await onDeactivate(rgi.geoItemId);
    if (ok) setConfirmOpen(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2">
        {!rgi.deactivatedAt && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(v2Route.areaInput.record.detail.edit.$buildPath({ params: { recordId, geoItemId } }))
            }
          >
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            navigate(
              v2Route.areaInput.record.create.$buildPath({
                params: { recordId },
                searchParams: { duplicateGeoItemId: geoItemId },
              }),
            )
          }
        >
          <Copy />
          <span>{t("feature.recordGeoItems.operation.duplicateArea")}</span>
        </Button>
        {!rgi.deactivatedAt && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmOpen(true)}>
            <PowerOff />
            <span>{t("ui.deactivate")}</span>
          </Button>
        )}
      </div>

      <AreaViewMap areaH3Indexes={areaH3Indexes} geometry={geometry} />

      <SectionCard icon={Info} title={t("ui.detail")} contentClassName="space-y-3 text-[13px]">
        <AreaDetailField label={t("model.common.id")}>
          <div className="flex items-center gap-2">
            <span className="font-mono tabular-nums text-fg-1">{rgi.geoItemId}</span>
            <CopyButton text={rgi.geoItemId} />
          </div>
        </AreaDetailField>
        <AreaGeoItemEntryList entries={entries} />
        {areaH3Indexes.length > 0 && (
          <AreaDetailField label={t("model.recordGeoItem.h3Indexes")}>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="tabular-nums text-fg-2">{areaH3Indexes.length}</span>
                <CopyButton text={areaH3Indexes.join(",")} />
              </div>
              <pre className="max-h-40 overflow-auto whitespace-pre rounded-[var(--radius-xs)] bg-bg-subtle p-2 text-12-regular leading-relaxed tabular-nums text-fg-1">
                {areaH3Indexes.join("\n")}
              </pre>
            </div>
          </AreaDetailField>
        )}
        {geometry && (
          <AreaDetailField label={t("model.recordGeoItem.geoJson")}>
            <div className="space-y-1.5">
              <CopyButton text={JSON.stringify(geometry, null, 2)} />
              <div className="max-w-full overflow-x-auto">
                <JsonTable target={geometry} />
              </div>
            </div>
          </AreaDetailField>
        )}
        {rgi.deactivatedAt && (
          <AreaDetailField label={t("ui.status", { defaultValue: "Status" })}>
            <Badge tone="default">{t("ui.deactivated")}</Badge>
          </AreaDetailField>
        )}
      </SectionCard>

      {evidences.length > 0 && (
        <SectionCard
          icon={FileText}
          title={t("model.evidence.modelNames", { defaultValue: "Evidence" })}
          contentClassName="space-y-2 text-[13px]"
        >
          {evidences.map((ev) => (
            <div key={ev.id} className="border-b border-border-default pb-2 last:border-b-0 last:pb-0">
              {ev.references.map((reference) => (
                <p key={reference.id} className="text-fg-1">
                  {reference.title}
                </p>
              ))}
              {(ev.citation as { page?: string } | null)?.page && (
                <p className="text-fg-3">
                  {t("model.evidence.page", { defaultValue: "p." })} {(ev.citation as { page?: string }).page}
                </p>
              )}
            </div>
          ))}
        </SectionCard>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ui.deactivateX", { x: t("ui.thisArea") })}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeactivate} disabled={processing}>
              {t("ui.ok")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      toast.success(t("feature.common.copied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-fg-2"
      onClick={onCopy}
      aria-label={t("ui.copy")}
    >
      <Copy className="h-3.5 w-3.5" />
    </Button>
  );
}
